import { defineComponent as _defineComponent } from 'vue'
import { computed as _computed } from 'vue';

import { computed, ref } from 'vue';
import { DataItemProps } from 'vuetify';
import { getModule } from 'vuex-module-decorators';
import { FieldLevelSearchDto, TagDto } from '@/api';
import type { AssetSearchCriteria } from '@/api';
import makeSafeForCSS from '@/utils/makeSafeCss';
import highlight from '@/utils/filters/highlight';
import enumToString from '@/utils/enumToString';
import { getProviderIconSrc } from '@/utils/provider-icons';
import store from '@/store';
import authModule from '@/store/modules/auth';
import router from '@/router';
import copyToClipboard from '@/utils/copyToClipboard';
import ConnectorSourceDestination from '@/components/asset/overview/Connector-Source-Destination.vue';
import ConnectorStatus from '@/components/asset/overview/Connector-Status.vue';
import TransformationStatus from '@/components/asset/overview/Transformation-Status.vue';
import OrchestrationStatus from '@/components/asset/overview/Orchestration-Status.vue';
import SAvatars from '@/components/SAvatars/SAvatars.vue';
import DataUsage from '@/components/usage/Usage.vue';
import i18n from '@/i18n';

interface CatalogResultsCardProps {
  searchParams: AssetSearchCriteria,
  itemProps: Omit<DataItemProps, 'item'> & { item: FieldLevelSearchDto }
  light?: boolean,
  isLast: boolean,
  hideActions: boolean,
  isEditable: boolean,
  isSelectable: boolean,
  shouldTargetBlank: boolean
}

type CatalogResultsCardEmits = {
  (event: 'edit', item: FieldLevelSearchDto): void
}


export default /*#__PURE__*/_defineComponent({
  __name: 'Catalog-Results-Card',
  props: {
  searchParams: null,
  itemProps: null,
  light: { type: Boolean, default: false },
  isLast: { type: Boolean, default: false },
  hideActions: { type: Boolean, default: false },
  isEditable: { type: Boolean, default: true },
  isSelectable: { type: Boolean, default: true },
  shouldTargetBlank: { type: Boolean, default: false }
},
  emits: ["edit"],
  setup(__props, { emit }) {

const props = __props;
const UNEDITABLE_ENTITY_TYPES = [FieldLevelSearchDto.entityType.DECLARED_ASSET];

const auth = getModule(authModule, store);




const card = ref<any | null>(null);

const item = _computed(() => props.itemProps.item);
const canEdit = computed(() => auth.userActions['metadata.asset.catalog-editor']);
const assetLink = computed(() => ({ name: 'data-catalog.asset.overview', params: { urn: item.value.urn! } }));
const imgsrc = computed(() => getProviderIconSrc(item.value.lineagePlatform));
const alt = computed(() => item.value.lineagePlatform);
const isDeclaredAsset = computed(() => item.value.entityType === FieldLevelSearchDto.entityType.DECLARED_ASSET);
const assetId = computed(() => item.value.id);
const assetUri = computed(() => item.value.uri);
const id = computed(() => makeSafeForCSS(`${item.value.name}_${assetId.value}`));
const textSearch = computed(() => props.searchParams.textSearch);
const name = computed(() => highlight(item.value.name, textSearch.value || ''));
const type = computed(() => enumToString(item.value.label));
const isUsageSupported = computed(() => item.value.usage && item.value.usage !== FieldLevelSearchDto.usage.UNSUPPORTED);
const isConnector = computed(() => item.value.connectorDto);
const isOrchestration = computed(() => item.value.dagRunDto);
const dagLastRunState = computed(() => item.value.dagRunDto?.state);
const showDbtModelLastRun = computed(() => item.value.dbtModelDto);
const dbtModelLastRunDate = computed(() => item.value.dbtModelDto?.lastRunDate);
const dbtModelLastRunStatus = computed(() => item.value.dbtModelDto?.lastRunStatus);
const description = computed(() => {
  const { description: itemDescription } = item.value;
  if (!itemDescription) return '';
  return highlight(item.value.description!, textSearch.value || '');
});
const disableCheckbox = _computed(() => {
  // Disable checkbox if the item is editable but the entity type is not
  if (props.isEditable) {
    return UNEDITABLE_ENTITY_TYPES.includes(item.value.entityType);
  }

  // disable the checkbox if the item is not selectable
  return !props.isSelectable;
});
const isSelected = _computed(() => {
  if (props.isEditable) {
    return props.itemProps.isSelected && !UNEDITABLE_ENTITY_TYPES.includes(item.value.entityType);
  }
  return props.itemProps.isSelected;
});

const attachableTags = (tags: TagDto[] | undefined) => tags || [];

const handleEdit = () => {
  emit('edit', item.value);
};

const copyUriToClipboard = () => {
  copyToClipboard(assetUri.value!, i18n.t('common.words.copy_to_clipboard_success_asset_uri', {
    value: assetUri.value!,
  }));
};

const navigateToAsset = (e: MouseEvent) => {
  if (e.metaKey || e.ctrlKey || props.shouldTargetBlank) {
    window.open(router.resolve(assetLink.value).href, '_blank');
  } else {
    router.push(assetLink.value);
  }
};

const elementType = computed(() => (props.shouldTargetBlank ? 'div' : 'router-link'));
const elementProps = computed(() => (props.shouldTargetBlank ? {} : { to: assetLink.value }));

return { __sfc: true,UNEDITABLE_ENTITY_TYPES, auth, props, emit, card, item, canEdit, assetLink, imgsrc, alt, isDeclaredAsset, assetId, assetUri, id, textSearch, name, type, isUsageSupported, isConnector, isOrchestration, dagLastRunState, showDbtModelLastRun, dbtModelLastRunDate, dbtModelLastRunStatus, description, disableCheckbox, isSelected, attachableTags, handleEdit, copyUriToClipboard, navigateToAsset, elementType, elementProps, _computed, computed, ref, DataItemProps, getModule, FieldLevelSearchDto, TagDto, makeSafeForCSS, highlight, enumToString, getProviderIconSrc, store, authModule, router, copyToClipboard, ConnectorSourceDestination, ConnectorStatus, TransformationStatus, OrchestrationStatus, SAvatars, DataUsage, i18n }
}

})